import * as React from "react";
import { ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AccountSelectPanel, type AccountOption } from "./account-select-panel";
import { useAccountLabelHelpers } from "./useAccountLabelHelpers";

export type { AccountOption } from "./account-select-panel";
export { AccountItemContent } from "./account-select-panel";

interface AccountSelectProps {
  accounts: AccountOption[];
  value?: string;
  onChange: (value: string, account?: AccountOption) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  disabled?: boolean;
  /** Category name(s) to show first in the list. Can be a single category or array for multi-level priority. */
  prioritizeCategory?: string | string[];
  /** Account type name(s) to show first, before category grouping. E.g., ["Bank Account", "Credit Card"] */
  prioritizeType?: string | string[];
  /** Account IDs to pin at the top of the dropdown in a separate group */
  pinnedAccountIds?: string[];
  /** Label for the pinned accounts group */
  pinnedLabel?: string;
  /**
   * Force currencies onto every account label. Pass
   * `useShowAccountCurrency(wholeChart)` when `accounts` is a filtered slice
   * of the chart. `false` hides them outright; omit the prop to keep the
   * list-based rule — see `useAccountLabelHelpers`.
   */
  showCurrency?: boolean;
  /** Id of the trigger button, so a `<Label htmlFor>` names it. */
  id?: string;
  /** Ids of the help / error text describing the field. */
  "aria-describedby"?: string;
  /** Marks the field invalid (pair it with an error in `aria-describedby`). */
  "aria-invalid"?: boolean;
}

export function AccountSelect({
  accounts,
  value,
  onChange,
  placeholder = "Select account",
  searchPlaceholder = "Search accounts...",
  emptyText = "No accounts found.",
  className,
  disabled = false,
  prioritizeCategory,
  prioritizeType,
  pinnedAccountIds,
  pinnedLabel = "Suggested",
  showCurrency,
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: AccountSelectProps) {
  const [open, setOpen] = React.useState(false);
  const { getDisplayName, buildTypeLabel } = useAccountLabelHelpers(accounts, { showCurrency });
  const selectedAccount = accounts.find((account) => account.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid || undefined}
          className={cn("w-full max-w-full justify-between font-normal overflow-hidden", className)}
          disabled={disabled}
        >
          <span className="truncate">
            {selectedAccount ? (
              <>
                <span>{getDisplayName(selectedAccount)}</span>
                <span className="ml-2 italic text-muted-foreground text-xs">
                  {buildTypeLabel(selectedAccount)}
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] overflow-hidden p-0" align="start">
        <AccountSelectPanel
          accounts={accounts}
          value={value}
          onSelect={(id, account) => {
            onChange(id, account);
            setOpen(false);
          }}
          searchPlaceholder={searchPlaceholder}
          emptyText={emptyText}
          prioritizeCategory={prioritizeCategory}
          prioritizeType={prioritizeType}
          pinnedAccountIds={pinnedAccountIds}
          pinnedLabel={pinnedLabel}
          showCurrency={showCurrency}
        />
      </PopoverContent>
    </Popover>
  );
}
